import React, { useRef, useState, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Info,
  RefreshCw,
  Clock,
  Users,
  CheckCircle2,
  XCircle,
  Download,
} from "lucide-react";
import * as XLSX from "xlsx";

const BulkUploadSection = ({ groupName, onUpload, onRefresh }) => {
  const fileInputRef = useRef(null);
  const workerRef = useRef(null);

  // Form state
  const [selectedFile, setSelectedFile] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);

  // UI state
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [validationResults, setValidationResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);
  const [cleanedFile, setCleanedFile] = useState(null);
  const [showCleaningOptions, setShowCleaningOptions] = useState(false);
  const [currentFileName, setCurrentFileName] = useState("");
  const [userChoice, setUserChoice] = useState(null);
  const [showAllFailedRows, setShowAllFailedRows] = useState(false);

  // File size limits and thresholds
  const FILE_SIZE_LIMITS = {
    MAX_SIZE: 5 * 1024 * 1024,
    LARGE_FILE_THRESHOLD: 2 * 1024 * 1024,
    PREVIEW_ROWS: 10,
    MAX_PREVIEW_ROWS: 1000,
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const convertScientificNotation = (value) => {
    if (!value) return "";
    const str = value.toString().trim();

    if (/^\d+\.?\d*[eE][+-]?\d+$/.test(str)) {
      try {
        const num = Number(str);
        if (!isNaN(num) && isFinite(num)) {
          return num.toLocaleString("fullwide", { useGrouping: false });
        }
      } catch (e) {
        console.warn("Failed to convert scientific notation:", str);
      }
    }
    return str;
  };

  const validatePhoneNumber = (phone) => {
    if (!phone || phone.toString().trim() === "") return false;
    const convertedPhone = convertScientificNotation(phone);
    const cleanPhone = convertedPhone.replace(/\D/g, "");
    return cleanPhone.length === 12 && cleanPhone.startsWith("233");
  };

  // Initialize Web Worker for file processing
  const initializeWorker = useCallback(() => {
    if (workerRef.current) {
      workerRef.current.terminate();
    }

    const workerCode = `
      importScripts('https://unpkg.com/xlsx@0.18.5/dist/xlsx.full.min.js');

      function validatePhoneNumber(phone) {
        if (!phone || phone.toString().trim() === '') return false;
        const phoneStr = phone.toString().trim();
        
        // Check if phone number contains spaces
        const hasSpaces = phoneStr.includes(' ');
        
        // Remove all non-digits to get clean phone number
        const cleanPhone = phoneStr.replace(/\\D/g, '');
        
        // If the clean phone number is valid, return success
        if (cleanPhone.length === 12 && cleanPhone.startsWith('233')) {
          return { isValid: true, hasSpaces: hasSpaces, cleanPhone: cleanPhone };
        }
        
        return { isValid: false, hasSpaces: hasSpaces, cleanPhone: cleanPhone };
      }

      function hasValidContent(row) {
        if (!row || row.length === 0) return false;
        return row.some(cell => cell && cell.toString().trim() !== '');
      }

      function isCompletelyEmpty(row) {
        if (!row || row.length === 0) return true;
        return !row.some(cell => cell && cell.toString().trim() !== '');
      }

      // Parses a birthday cell into a strict "YYYY-MM-DD" string.
      // This worker reads cells with raw:false, so dates already arrive as
      // pre-formatted strings (not numeric Excel serial values).
      // Blank is always valid (birthday is optional). An unparseable value is
      // NOT treated as a fatal error for the row - the caller decides whether
      // to warn about it, the contact still gets saved without a birthday.
      function parseBirthdayCell(cellValue) {
        if (cellValue === undefined || cellValue === null) {
          return { formattedBirthday: '', isValid: true };
        }
        const trimmedValue = cellValue.toString().trim();
        if (trimmedValue === '') {
          return { formattedBirthday: '', isValid: true };
        }

        // Normalize common separators ("/" or ".") to "-" before matching.
        const normalizedValue = trimmedValue.replace(/[/.]/g, '-');
        const isoDateMatch = normalizedValue.match(/^(\\d{4})-(\\d{2})-(\\d{2})$/);
        if (!isoDateMatch) {
          return { formattedBirthday: '', isValid: false };
        }

        const birthdayYear = parseInt(isoDateMatch[1], 10);
        const birthdayMonth = parseInt(isoDateMatch[2], 10);
        const birthdayDay = parseInt(isoDateMatch[3], 10);
        const parsedDate = new Date(birthdayYear, birthdayMonth - 1, birthdayDay);
        const isRealCalendarDate =
          parsedDate.getFullYear() === birthdayYear &&
          parsedDate.getMonth() === birthdayMonth - 1 &&
          parsedDate.getDate() === birthdayDay;
        const isInReasonableRange = birthdayYear >= 1900 && parsedDate <= new Date();

        if (!isRealCalendarDate || !isInReasonableRange) {
          return { formattedBirthday: '', isValid: false };
        }

        return { formattedBirthday: normalizedValue, isValid: true };
      }

      function processExcelFile(file) {
        const reader = new FileReader();
        reader.onload = function(e) {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];
            const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
              header: 1, 
              defval: '',
              raw: false
            });
            processRowData(jsonData, file.name);
          } catch (error) {
            self.postMessage({
              type: 'error',
              error: 'Failed to parse Excel file: ' + error.message
            });
          }
        };
        reader.readAsArrayBuffer(file);
      }

      function processRowData(data, fileName) {
        let validContacts = [];
        let failedRows = [];
        let birthdayWarnings = [];
        let totalRows = data.length;
        let processedRows = 0;
        let emptyRowsCleaned = 0;

        const processedPhones = new Set();

        data.forEach((row, index) => {
          processedRows++;

          if (index === 0) return;

          if (isCompletelyEmpty(row)) {
            emptyRowsCleaned++;
            return;
          }

          if (hasValidContent(row)) {
            const phoneNumber = row[0] ? row[0].toString().trim() : '';
            const firstName = row[1] ? row[1].toString().trim() : '';
            const lastName = row[2] ? row[2].toString().trim() : '';
            const email = row[3] ? row[3].toString().trim() : '';
            const birthdayCell = row[4] !== undefined ? row[4].toString().trim() : '';
            const birthdayResult = parseBirthdayCell(birthdayCell);
            if (!birthdayResult.isValid && birthdayCell !== '') {
              birthdayWarnings.push({ rowNumber: index + 1, phoneNumber: phoneNumber, rawBirthdayValue: birthdayCell });
            }

            if (phoneNumber === '' && (firstName !== '' || lastName !== '' || email !== '' || birthdayResult.formattedBirthday !== '')) {
              failedRows.push({
                rowNumber: index + 1,
                phoneNumber: phoneNumber || '[Empty]',
                reason: 'Missing phone number but has other contact data',
                hasOtherData: true,
                firstName: firstName,
                lastName: lastName,
                email: email,
                birthday: birthdayResult.formattedBirthday
              });
              return;
            }
            
            const validation = validatePhoneNumber(phoneNumber);
            
            // Check if the cleaned phone number is valid (12 digits starting with 233)
            const isCleanPhoneValid = validation.cleanPhone.length === 12 && validation.cleanPhone.startsWith('233');
            
            if (isCleanPhoneValid && !validation.hasSpaces) {
              // Only consider valid if no spaces and clean phone is valid
              if (processedPhones.has(validation.cleanPhone)) {
                failedRows.push({
                  rowNumber: index + 1,
                  phoneNumber: phoneNumber,
                  reason: 'Duplicate phone number'
                });
                return;
              }
              
              processedPhones.add(validation.cleanPhone);

              validContacts.push({
                phone_number: validation.cleanPhone,
                first_name: firstName,
                last_name: lastName,
                email: email,
                birthday: birthdayResult.formattedBirthday
              });
            } else if (isCleanPhoneValid && validation.hasSpaces) {
              // Phone number would be valid if spaces were removed - add to failed rows
              failedRows.push({
                rowNumber: index + 1,
                phoneNumber: phoneNumber,
                reason: 'Phone number contains spaces - can be cleaned',
                hasSpaces: true,
                cleanPhone: validation.cleanPhone, // Store the cleaned version for later use
                firstName: firstName,
                lastName: lastName,
                email: email,
                birthday: birthdayResult.formattedBirthday
              });
            } else {
              let reason;
              if (phoneNumber === '') {
                reason = 'Empty phone number';
              } else if (validation.cleanPhone.length !== 12) {
                reason = \`Invalid phone number (\${validation.cleanPhone.length} digits, need 12)\`;
              } else if (!validation.cleanPhone.startsWith('233')) {
                reason = 'Phone number must start with 233';
              } else {
                reason = 'Invalid phone number format';
              }
              failedRows.push({
                rowNumber: index + 1,
                phoneNumber: phoneNumber,
                reason: reason,
                hasSpaces: validation.hasSpaces
              });
            }
          }
          
          if (processedRows % 1000 === 0 || processedRows === totalRows) {
            const progress = (processedRows / totalRows) * 100;
            self.postMessage({
              type: 'progress',
              progress: progress,
              processed: processedRows
            });
          }
        });

        self.postMessage({
          type: 'validationComplete',
          data: {
            validContacts,
            failedRows,
            birthdayWarnings,
            totalProcessed: totalRows,
            successCount: validContacts.length,
            failedCount: failedRows.length,
            emptyRowsCleaned: emptyRowsCleaned
          }
        });
      }

      function isExcelFile(fileName) {
        return /\\.(xlsx|xls)$/i.test(fileName);
      }

      self.onmessage = function(e) {
        const { file, action } = e.data;
        
        if (action === 'validateContacts') {
          if (isExcelFile(file.name)) {
            processExcelFile(file);
          } else {
            self.postMessage({
              type: 'error',
              error: 'Unsupported file format. Please use Excel files (.xlsx or .xls)'
            });
          }
        }
      };
    `;

    const blob = new Blob([workerCode], { type: "application/javascript" });
    workerRef.current = new Worker(URL.createObjectURL(blob));

    workerRef.current.onmessage = (e) => {
      const { type, data, error, progress } = e.data;

      if (type === "validationComplete") {
        setValidationResults(data);

        // Check if any failed rows have spaces that can be cleaned
        const failedRowsWithSpaces = data.failedRows.filter(
          (row) => row.hasSpaces && row.cleanPhone
        ).length;
        const totalSpacesIssues = failedRowsWithSpaces;

        if (
          data.successCount === 0 &&
          data.failedRows.filter((row) => row.hasSpaces && row.cleanPhone)
            .length === 0
        ) {
          setValidationErrors([
            "No valid phone numbers found in the file.",
            "Phone numbers must be 12 digits and start with 233.",
            "Please check your file format and try again.",
          ]);
          setShowCleaningOptions(false);
          setUserChoice(null);
        } else if (
          data.failedCount > 0 ||
          totalSpacesIssues > 0 ||
          data.failedRows.some((row) => row.hasSpaces)
        ) {
          const originalFileName =
            selectedFile?.name || currentFileName || "contacts.xlsx";

          // Create enhanced valid contacts by including phone numbers with spaces that can be cleaned
          const enhancedValidContacts = [...data.validContacts];

          // Add failed rows with spaces that can be cleaned
          data.failedRows.forEach((failedRow) => {
            if (failedRow.hasSpaces && failedRow.cleanPhone) {
              enhancedValidContacts.push({
                phone_number: failedRow.cleanPhone,
                first_name: failedRow.firstName || "",
                last_name: failedRow.lastName || "",
                email: failedRow.email || "",
                birthday: failedRow.birthday || "",
                hadSpaces: true,
              });
            }
          });

          const cleaned = createCleanedFile(
            enhancedValidContacts,
            originalFileName
          );
          setCleanedFile(cleaned);
          setShowCleaningOptions(true);
          setUserChoice(null);

          const emptyRowsMessage =
            data.emptyRowsCleaned > 0
              ? ` (${data.emptyRowsCleaned} empty rows automatically cleaned)`
              : "";

          let spacesMessage = "";
          if (failedRowsWithSpaces > 0) {
            spacesMessage += ` ${failedRowsWithSpaces} phone number${
              failedRowsWithSpaces > 1 ? "s" : ""
            } contain${
              failedRowsWithSpaces > 1 ? "" : "s"
            } spaces and can be cleaned`;
          }

          setValidationErrors([
            `Found ${data.successCount} valid contacts and ${data.failedCount} invalid entries${emptyRowsMessage}.${spacesMessage}`,
            "Please choose how to proceed below.",
          ]);
        } else {
          if (data.emptyRowsCleaned > 0) {
            const originalFileName =
              selectedFile?.name || currentFileName || "contacts.xlsx";
            const fullyCleanedFile = createFullyCleanedFile(
              data.validContacts,
              originalFileName
            );
            setSelectedFile(fullyCleanedFile);
            setValidationErrors([
              `${data.emptyRowsCleaned} empty rows were automatically cleaned from your file.`,
              "A cleaned version of your file will be uploaded (no empty rows).",
            ]);
          } else {
            setValidationErrors([]);
          }
          setShowCleaningOptions(false);
          setUserChoice("cleaned");
        }

        // Non-blocking heads-up about birthday cells that couldn't be parsed
        // (the affected contacts are still saved, just without a birthday).
        if (data.birthdayWarnings && data.birthdayWarnings.length > 0) {
          setValidationErrors((previousErrors) => [
            ...previousErrors,
            `${data.birthdayWarnings.length} row(s) had a birthday we couldn't understand and were saved without one (expected format: YYYY-MM-DD).`,
          ]);
        }

        setShowResults(true);
        setIsProcessing(false);
        setProcessingProgress(100);
      } else if (type === "progress") {
        setProcessingProgress(progress);
      } else if (type === "error") {
        setValidationErrors([error]);
        setError(error);
        setIsProcessing(false);
      }
    };

    return () => {
      if (workerRef.current) {
        workerRef.current.terminate();
      }
    };
  }, [selectedFile, currentFileName]);

  let processingStartTime = 0;

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat().format(num);
  };

  // Create cleaned Excel file with only valid contacts
  const createCleanedFile = (validContacts, originalFileName) => {
    const headers = ["Phone Number", "First Name", "Last Name", "Email", "Birthday"];
    const dataRows = validContacts.map((contact) => [
      contact.phone_number || "",
      contact.first_name || "",
      contact.last_name || "",
      contact.email || "",
      contact.birthday || "",
    ]);

    const worksheetData = [headers, ...dataRows];
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    const range = XLSX.utils.decode_range(ws["!ref"] || "A1");
    for (let R = range.s.r; R <= range.e.r; ++R) {
      for (let C = range.s.c; C <= range.e.c; ++C) {
        const cell_address = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cell_address] || ws[cell_address].v === "") {
          delete ws[cell_address];
        }
      }
    }

    ws["!cols"] = [
      { width: 15 }, // Phone Number
      { width: 15 }, // First Name
      { width: 15 }, // Last Name
      { width: 25 }, // Email
      { width: 15 }, // Birthday
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Cleaned Contacts");

    const fileBuffer = XLSX.write(wb, {
      bookType: "xlsx",
      type: "array",
      compression: true,
      bookSST: false,
      cellStyles: false,
    });

    const blob = new Blob([fileBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const cleanedFileName = originalFileName.replace(
      /\.(xlsx|xls)$/i,
      "_cleaned.xlsx"
    );

    return new File([blob], cleanedFileName, { type: blob.type });
  };

  // Create cleaned file with all valid data
  const createFullyCleanedFile = (validContacts, originalFileName) => {
    return createCleanedFile(validContacts, originalFileName);
  };

  const handleFileSelect = (file) => {
    // Reset previous state
    setError(null);
    setSuccess(false);
    setValidationResults(null);
    setShowResults(false);
    setValidationErrors([]);
    setCleanedFile(null);
    setShowCleaningOptions(false);
    setUserChoice(null);
    setShowAllFailedRows(false);
    setOriginalFile(null);

    if (!file) return;

    // Enhanced file type validation
    const validTypes = [
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];

    const validExtensions = /\.(xls|xlsx)$/i;
    const fileExtension = file.name.toLowerCase();

    // Check if it's an image file
    if (
      file.type.startsWith("image/") ||
      /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(fileExtension)
    ) {
      setError(
        "Image files are not supported. Please upload an Excel file (.xlsx or .xls)"
      );
      return;
    }

    // Check if it's a Word document
    if (file.type.includes("word") || /\.(doc|docx)$/i.test(fileExtension)) {
      setError(
        "Word documents are not supported. Please upload an Excel file (.xlsx or .xls)"
      );
      return;
    }

    // Check if it's a PDF
    if (file.type === "application/pdf" || fileExtension.endsWith(".pdf")) {
      setError(
        "PDF files are not supported. Please upload an Excel file (.xlsx or .xls)"
      );
      return;
    }

    // Check if it's a text/CSV file
    if (
      file.type === "text/csv" ||
      file.type === "text/plain" ||
      fileExtension.endsWith(".csv") ||
      fileExtension.endsWith(".txt")
    ) {
      setError(
        "CSV and text files are not supported. Please upload an Excel file (.xlsx or .xls)"
      );
      return;
    }

    // Check if it's a PowerPoint file
    if (
      file.type.includes("powerpoint") ||
      /\.(ppt|pptx)$/i.test(fileExtension)
    ) {
      setError(
        "PowerPoint files are not supported. Please upload an Excel file (.xlsx or .xls)"
      );
      return;
    }

    // Check if it's a ZIP or compressed file
    if (
      file.type.includes("zip") ||
      file.type.includes("compressed") ||
      /\.(zip|rar|7z|tar|gz)$/i.test(fileExtension)
    ) {
      setError(
        "Compressed files are not supported. Please upload an Excel file (.xlsx or .xls)"
      );
      return;
    }

    // Final check for Excel files
    if (!validTypes.includes(file.type) && !validExtensions.test(file.name)) {
      setError(
        "Please select a valid Excel file (.xlsx or .xls). Other file types are not supported."
      );
      return;
    }

    // File size validation
    if (file.size > FILE_SIZE_LIMITS.MAX_SIZE) {
      setError(
        `File size must be less than ${Math.round(
          FILE_SIZE_LIMITS.MAX_SIZE / (1024 * 1024)
        )}MB. Your file is ${formatFileSize(file.size)}.`
      );
      return;
    }

    // Set file and start processing
    setSelectedFile(file);
    setOriginalFile(file);
    setCurrentFileName(file.name);
    setIsProcessing(true);
    setProcessingProgress(0);
    processingStartTime = Date.now();

    // Initialize worker and process file
    initializeWorker();

    // Start validation
    setTimeout(() => {
      if (workerRef.current) {
        workerRef.current.postMessage({
          file,
          action: "validateContacts",
        });
      }
    }, 100);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadProgress(0);
    setError(null);
    setSuccess(false);
    // Simulate progress for UX
    const progressInterval = setInterval(() => {
      setUploadProgress((prev) => {
        if (prev >= 95) {
          clearInterval(progressInterval);
          return prev;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
    try {
      await onUpload(selectedFile, groupName);
      clearInterval(progressInterval);
      setUploadProgress(100);
      setSuccess(true);
      setSelectedFile(null);
    } catch (e) {
      clearInterval(progressInterval);
      setError("Failed to upload contacts. Please try again.");
    } finally {
      setUploading(false);
      setTimeout(() => setUploadProgress(0), 1000);
    }
  };

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
      >
        <div className="flex gap-3 items-center mb-6">
          <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg">
            <Upload className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              Bulk Upload Contacts
            </h2>
            <p className="text-gray-600">
              Add multiple contacts to{" "}
              <span className="font-semibold">{groupName}</span> by uploading an
              Excel file.
            </p>
          </div>
        </div>
        <div
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          className={`relative border-2 border-dashed rounded-xl p-6 transition-all duration-200 ${
            dragActive
              ? "bg-red-50 border-red-400"
              : error
              ? "bg-red-50 border-red-300"
              : "border-gray-300 hover:border-gray-400"
          }`}
        >
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => handleFileSelect(e.target.files[0])}
            accept=".xls,.xlsx"
            className="hidden"
            disabled={uploading}
          />
          <div className="text-center">
            {selectedFile ? (
              <div className="flex flex-col gap-3 justify-center items-center sm:flex-row sm:gap-4">
                <div className="flex-shrink-0 p-4 bg-red-100 rounded-lg">
                  <FileText className="w-8 h-8 text-red-600" />
                </div>
                <div className="flex-1 min-w-0 text-center sm:text-left">
                  <p
                    className="text-base font-medium text-gray-900 truncate"
                    title={selectedFile.name}
                  >
                    {selectedFile.name}
                  </p>
                  <p className="text-sm text-gray-500">
                    {(selectedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
                <button
                  onClick={() => {
                    setSelectedFile(null);
                    // Reset all validation state when file is removed
                    setShowResults(false);
                    setValidationResults(null);
                    setValidationErrors([]);
                    setCleanedFile(null);
                    setShowCleaningOptions(false);
                    setUserChoice(null);
                    setShowAllFailedRows(false);
                    setOriginalFile(null);
                    setError(null);
                    setSuccess(false);
                    setUploadProgress(0);
                    setProcessingProgress(0);
                    setIsProcessing(false);
                  }}
                  className="flex-shrink-0 p-2 text-red-600 rounded-lg transition-colors duration-200 hover:bg-red-100"
                  disabled={uploading}
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            ) : (
              <>
                <div className="inline-block p-4 mb-3 bg-gray-100 rounded-lg">
                  <Upload className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-gray-900">
                  <span className="hidden sm:inline">
                    Drop your file here, or{" "}
                  </span>
                  <span className="text-red-600">
                    <span className="sm:hidden">Tap to </span>browse
                  </span>
                </h3>
                <p className="mb-4 text-sm text-gray-500">
                  Upload an Excel file (.xlsx) with your contacts. The file
                  should have columns for:
                  <strong>
                    {" "}
                    Phone Number, First Name, Last Name, Email, Birthday
                  </strong>{" "}
                  (in that order).
                  <br />
                  <span className="font-medium text-orange-600">
                    Phone numbers must be 12 digits starting with 233. Spaces
                    will be automatically removed.
                  </span>{" "}
                  <span className="text-gray-500">
                    Birthday is optional — format as YYYY-MM-DD.
                  </span>
                </p>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
                  disabled={uploading}
                >
                  Choose File
                </button>
              </>
            )}
          </div>
          {/* Processing Progress */}
          {isProcessing && (
            <div className="p-4 mt-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-3 items-center mb-3">
                <div className="w-5 h-5 rounded-full border-b-2 border-blue-600 animate-spin"></div>
                <span className="text-sm font-medium text-gray-900">
                  Processing file...
                </span>
                <span className="ml-auto text-sm text-gray-500">
                  {Math.round(processingProgress)}%
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-blue-600 rounded-full transition-all duration-300"
                  style={{ width: `${processingProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Upload Progress */}
          {uploading && (
            <div className="p-4 mt-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex gap-3 items-center">
                <div className="w-5 h-5 rounded-full border-b-2 border-red-600 animate-spin"></div>
                <span className="text-sm font-medium text-gray-900">
                  Uploading contacts...
                </span>
                <span className="ml-auto text-sm text-gray-500">
                  {uploadProgress}%
                </span>
              </div>
              <div className="mt-2 w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-gradient-to-r from-red-500 to-red-600 rounded-full transition-all duration-300"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {(error || validationErrors.length > 0) && (
            <div className="p-3 mt-4 bg-amber-50 rounded-lg border border-amber-200">
              {error && (
                <p className="flex gap-2 items-center mb-2 text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4" />
                  {error}
                </p>
              )}
              {validationErrors.length > 0 && (
                <div className="space-y-1">
                  {validationErrors.map((error, index) => (
                    <p
                      key={index}
                      className="flex gap-2 items-center text-sm text-amber-700"
                    >
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 mt-4 bg-red-50 rounded-lg border border-red-200">
              <p className="flex gap-2 items-center text-sm text-red-700">
                <CheckCircle className="w-4 h-4" />
                Contacts uploaded successfully! Processing...
              </p>
            </div>
          )}
        </div>
      </motion.div>

      {/* Validation Results */}
      {showResults && validationResults && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
        >
          <div className="flex gap-3 items-center mb-6">
            <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Validation Results
              </h3>
              <p className="text-sm text-gray-600">
                {formatNumber(validationResults.totalProcessed)} rows processed
                {validationResults.emptyRowsCleaned > 0 && (
                  <span className="text-blue-600">
                    {" "}
                    • {formatNumber(validationResults.emptyRowsCleaned)} empty
                    rows cleaned
                  </span>
                )}
              </p>
            </div>
          </div>

          {/* Success/Failure Summary */}
          <div className="grid grid-cols-1 gap-4 mb-6 sm:grid-cols-3">
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
                  <Users className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-blue-800">
                    Total Contacts
                  </p>
                  <p className="text-2xl font-bold text-blue-900">
                    {formatNumber(validationResults.totalProcessed - 1)}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg">
                  <CheckCircle2 className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Valid Contacts
                  </p>
                  <p className="text-2xl font-bold text-red-900">
                    {formatNumber(validationResults.successCount)}
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <div className="flex gap-3 items-center">
                <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-lg">
                  <XCircle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Failed Contacts
                  </p>
                  <p className="text-2xl font-bold text-red-900">
                    {formatNumber(validationResults.failedCount)}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Failed Rows Details */}
          {validationResults.failedCount > 0 && (
            <div className="p-4 bg-red-50 rounded-lg border border-red-200">
              <h3 className="mb-3 text-sm font-medium text-red-800">
                Failed Rows Details
              </h3>
              <div className="overflow-y-auto max-h-40">
                <div className="space-y-2">
                  {validationResults.failedRows
                    .slice(
                      0,
                      showAllFailedRows
                        ? validationResults.failedRows.length
                        : 5
                    )
                    .map((failedRow, index) => (
                      <div key={index} className="space-y-1">
                        <div className="flex justify-between items-center text-xs">
                          <span className="text-red-700">
                            Row {failedRow.rowNumber}: {failedRow.phoneNumber}
                          </span>
                          <span className="text-red-600">
                            {failedRow.reason}
                          </span>
                        </div>
                        {failedRow.hasOtherData && (
                          <div className="ml-4 text-xs italic text-red-500">
                            Data:{" "}
                            {[
                              failedRow.firstName,
                              failedRow.lastName,
                              failedRow.email,
                            ]
                              .filter(Boolean)
                              .join(", ")}
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>
              {validationResults.failedRows.length > 5 && (
                <div className="pt-3 mt-3 border-t border-red-200">
                  <button
                    onClick={() => setShowAllFailedRows(!showAllFailedRows)}
                    className="flex gap-1 items-center text-xs font-medium text-red-600 transition-colors duration-200 hover:text-red-800"
                  >
                    {showAllFailedRows ? (
                      <>
                        Show Less
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 15l7-7 7 7"
                          />
                        </svg>
                      </>
                    ) : (
                      <>
                        Show {validationResults.failedRows.length - 5} More
                        Failed Rows
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Action Buttons */}
        </motion.div>
      )}

      {/* File Cleaning Options */}
      {showCleaningOptions && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-6 bg-white rounded-2xl border border-gray-100 shadow-sm"
        >
          <div className="flex gap-3 items-center mb-4">
            <div className="flex justify-center items-center w-10 h-10 bg-blue-100 rounded-lg">
              <AlertCircle className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                File Cleaning Options
              </h3>
              <p className="text-sm text-gray-600">
                Choose how to handle invalid entries
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {/* Download Cleaned File Option */}
            <div className="p-4 rounded-lg border border-gray-200 transition-colors hover:border-blue-300">
              <div className="flex gap-3 items-center mb-3">
                <div className="flex justify-center items-center w-8 h-8 bg-red-100 rounded-lg">
                  <Download className="w-4 h-4 text-red-600" />
                </div>
                <h4 className="font-medium text-gray-900">Get Cleaned File</h4>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                Download a new Excel file with valid contacts. You can review
                and fix any issues manually.
              </p>
              <button
                onClick={() => {
                  if (!cleanedFile) return;
                  const url = URL.createObjectURL(cleanedFile);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = cleanedFile.name;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
              >
                <Download className="w-4 h-4" />
                Get Cleaned File
              </button>
            </div>

            {/* Proceed with Cleaned Data Option */}
            <div className="p-4 rounded-lg border border-gray-200 transition-colors hover:border-blue-300">
              <div className="flex gap-3 items-center mb-3">
                <div className="flex justify-center items-center w-8 h-8 bg-blue-100 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-blue-600" />
                </div>
                <h4 className="font-medium text-gray-900">Use Cleaned Data</h4>
              </div>
              <p className="mb-4 text-sm text-gray-600">
                Proceed with only the valid contacts. Invalid entries will be
                automatically removed.
              </p>
              <button
                onClick={() => {
                  if (!cleanedFile) return;

                  // Calculate total contacts after cleaning (including spaces-cleaned contacts)
                  const spacesCleanedCount =
                    validationResults?.failedRows?.filter(
                      (row) => row.hasSpaces && row.cleanPhone
                    ).length || 0;
                  const totalContactsAfterCleaning =
                    validationResults.successCount + spacesCleanedCount;

                  setSelectedFile(cleanedFile);
                  setShowCleaningOptions(false);
                  setValidationErrors([]);
                  setUserChoice("cleaned");
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors duration-200 font-medium text-sm"
              >
                <CheckCircle className="w-4 h-4" />
                Use Cleaned Data
              </button>
            </div>

            {/* Proceed with Original File Option */}
            <div
              className={`p-4 rounded-lg border transition-colors ${
                (validationResults?.failedRows?.filter(
                  (row) => row.hasSpaces && row.cleanPhone
                ).length || 0) > 0
                  ? "border-gray-200 bg-gray-50"
                  : "border-gray-200 hover:border-blue-300"
              }`}
            >
              <div className="flex gap-3 items-center mb-3">
                <div
                  className={`flex justify-center items-center w-8 h-8 rounded-lg ${
                    (validationResults?.failedRows?.filter(
                      (row) => row.hasSpaces && row.cleanPhone
                    ).length || 0) > 0
                      ? "bg-gray-100"
                      : "bg-orange-100"
                  }`}
                >
                  <FileText
                    className={`w-4 h-4 ${
                      (validationResults?.failedRows?.filter(
                        (row) => row.hasSpaces && row.cleanPhone
                      ).length || 0) > 0
                        ? "text-gray-400"
                        : "text-orange-600"
                    }`}
                  />
                </div>
                <h4
                  className={`font-medium ${
                    (validationResults?.failedRows?.filter(
                      (row) => row.hasSpaces && row.cleanPhone
                    ).length || 0) > 0
                      ? "text-gray-400"
                      : "text-gray-900"
                  }`}
                >
                  Use Original File
                </h4>
              </div>
              <p
                className={`mb-4 text-sm ${
                  (validationResults?.failedRows?.filter(
                    (row) => row.hasSpaces && row.cleanPhone
                  ).length || 0) > 0
                    ? "text-gray-400"
                    : "text-gray-600"
                }`}
              >
                {(validationResults?.failedRows?.filter(
                  (row) => row.hasSpaces && row.cleanPhone
                ).length || 0) > 0
                  ? "Cannot use original file when phone numbers contain spaces"
                  : "Proceed with the original file as-is. Backend will handle validation and processing."}
              </p>
              <button
                onClick={() => {
                  // Check if there are any phone numbers with spaces that can be cleaned
                  const failedRowsWithSpaces =
                    validationResults?.failedRows?.filter(
                      (row) => row.hasSpaces && row.cleanPhone
                    ).length || 0;
                  const totalSpacesIssues = failedRowsWithSpaces;

                  if (totalSpacesIssues > 0) {
                    // Prevent proceeding with original file when spaces are detected
                    // The button is already disabled, so this shouldn't be reachable
                    return;
                  }

                  if (originalFile) {
                    setSelectedFile(originalFile);
                  }
                  setShowCleaningOptions(false);
                  setValidationErrors([]);
                  setUserChoice("original");
                }}
                disabled={
                  (validationResults?.failedRows?.filter(
                    (row) => row.hasSpaces && row.cleanPhone
                  ).length || 0) > 0
                }
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg transition-colors duration-200 font-medium text-sm ${
                  (validationResults?.failedRows?.filter(
                    (row) => row.hasSpaces && row.cleanPhone
                  ).length || 0) > 0
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-orange-600 hover:bg-orange-700 text-white"
                }`}
              >
                <FileText className="w-4 h-4" />
                Use Original File
              </button>
            </div>
          </div>
        </motion.div>
      )}
      <div className="flex gap-3 mt-6">
        <button
          onClick={handleUpload}
          disabled={
            !selectedFile ||
            uploading ||
            isProcessing ||
            (showCleaningOptions && !userChoice)
          }
          className="flex flex-1 gap-2 justify-center items-center px-6 py-3 font-medium text-white bg-gradient-to-r from-red-500 to-red-600 rounded-xl transition-all hover:from-red-600 hover:to-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Upload className="w-5 h-5" />
          <span>
            {showCleaningOptions && !userChoice
              ? "Please Choose Option Above"
              : "Upload File"}
          </span>
        </button>
        <button
          onClick={onRefresh}
          className="flex gap-2 items-center px-6 py-3 font-medium text-red-700 bg-red-50 rounded-xl border border-red-200 transition-colors hover:bg-red-100"
        >
          <RefreshCw className="w-5 h-5" />
          <span>Refresh</span>
        </button>
      </div>
      {/* Tips Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="p-6 bg-red-50 rounded-2xl border border-red-200"
      >
        <div className="flex gap-3 items-start">
          <div className="flex flex-shrink-0 justify-center items-center w-8 h-8 bg-red-100 rounded-lg">
            <Info className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h3 className="mb-2 font-semibold text-red-800">
              Bulk Upload Tips
            </h3>
            <ul className="space-y-1 text-sm text-red-700">
              <li>• Only Excel files (.xlsx, .xls) are supported</li>
              <li>• Maximum file size is 5MB</li>
              <li>• Phone numbers must be 12 digits starting with 233</li>
              <li>• Remove empty rows for best performance</li>
              <li>• Download the template for correct formatting</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BulkUploadSection;
